import React from "react";
import { Box, Button, CircularProgress, Stack, Typography, alpha, useTheme } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { useUnlockProgramMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

// Shared banner for both program editor surfaces (ProgramLibrary/Create
// field editor + Program/View targets tab). Rendered when the program is
// locked; hidden when it is not.
//
// Reads as a STATE, not an error — neutral primary tint, not the red
// alert treatment. A locked program is correctly configured; the editor
// is just deliberately unavailable until unlocked.
//
// Unlock is not gated client-side. The server enforces
// `permission:Program_Edit` on POST /program/{id}/unlock (routes/api.php
// L743) and the library row kebab uses the same "no client gate, rely on
// server 403" pattern. The mutation invalidates the "program" tag, which
// both editor queries (useGetProgramQuery) provide — so the editor
// becomes editable without a reload the moment the unlock resolves.
type Props = {
  programId: number | string;
  programName?: string | null;
  // What is unavailable on THIS surface. One or two words, lowercase.
  // "editing this program" for the field editor; "editing this program's
  // targets" for the targets tab. Kept surface-specific so the banner
  // doesn't lie about scope.
  disabledSurfaceLabel: string;
};

const ProgramLockedBanner: React.FC<Props> = ({ programId, programName, disabledSurfaceLabel }) => {
  const theme = useTheme();
  const { showMessage, showErrorMessage } = useNotifier();
  const [unlockProgram, { isLoading }] = useUnlockProgramMutation();

  const handleUnlock = async () => {
    try {
      const res = await unlockProgram(programId).unwrap();
      if (res.status) showMessage(res.message || "Program unlocked.");
      else showErrorMessage(res.message || "Failed to unlock program.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to unlock program.");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        color: theme.palette.text.primary,
      }}
      role="status"
    >
      <LockOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 22, flexShrink: 0 }} />
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
          {programName ? `${programName} is locked.` : "This program is locked."}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.4 }}>
          {`Locked programs are read-only. Unlock to enable ${disabledSurfaceLabel}.`}
        </Typography>
      </Stack>
      <Button
        variant="contained"
        size="small"
        onClick={handleUnlock}
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <LockOpenOutlinedIcon />}
        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, flexShrink: 0 }}
      >
        {isLoading ? "Unlocking…" : "Unlock"}
      </Button>
    </Box>
  );
};

export default ProgramLockedBanner;
